// Modèle de la MULTI-SÉLECTION dans l'add-in : ce qu'un élément sélectionné
// transporte, comment l'ordre visuel est retrouvé, et quelles sélections sont
// redondantes.
//
// Tout ici est PUR et sans React, parce que c'est la partie qui peut être fausse
// sans que rien ne se voie à l'écran : un déplacement qui emporte un mail ET le
// dossier qui le contient, une plage MAJ qui saute des lignes, un identifiant
// perdu. Les composants, eux, ne font que dessiner.

/** Un mail rangé, ou un dossier. Les deux se sélectionnent et se déplacent. */
export type SelectedKind = 'mail' | 'folder';

export interface SelectedRef {
  kind: SelectedKind;
  id: string;
  containerId: string;
  /**
   * Le PARENT : pour un mail, le dossier qui le contient ; pour un dossier, son
   * dossier parent. `null` dans les deux cas = directement à la racine de
   * l'espace.
   *
   * Sert à deux choses qu'on ne peut pas déduire autrement : ne pas déplacer un
   * élément là où il est déjà, et repérer les sélections redondantes.
   */
  parentId: string | null;
  /** Libellé affiché, pour les comptes rendus (« 4 déplacés, 1 refusé : … »). */
  label: string;
}

/**
 * Les trois PORTÉES de sélection de l'add-in, nommées ici pour que l'arbre, la
 * page détail et la recherche ne puissent pas se tromper d'une lettre.
 *
 * L'arbre est découpé PAR ESPACE et non global : une plage MAJ qui traverserait
 * deux projets, ou un « Supprimer 6 éléments » portant sur deux espaces à la fois,
 * n'est pas ce que l'utilisateur croit désigner en balayant une liste.
 */
export const treeScope = (containerId: string): string => `tree:${containerId}`;
/** `folderId` vaut `null` sur la racine d'un espace, qui est une page comme une autre. */
export const detailScope = (containerId: string, folderId: string | null): string =>
  `detail:${containerId}:${folderId ?? 'root'}`;

/**
 * La recherche : UNE SEULE portée pour toute la liste de résultats, et donc —
 * seule des trois — une portée qui TRAVERSE LES ESPACES.
 *
 * C'est assumé, pas un oubli du découpage ci-dessus. Une liste de résultats est
 * par nature transverse : y interdire une plage MAJ entre deux espaces
 * reviendrait à découper visuellement une liste que l'utilisateur lit d'un seul
 * tenant. Et le lot le supporte déjà — `useBatchActions.move` passe
 * `newContainerId` quand l'élément change d'espace, et la suppression ne
 * s'adresse qu'à des identifiants.
 *
 * LE TERME ET L'ONGLET DE FILTRE EN FONT PARTIE, et c'est ce qui remplace un
 * nettoyage explicite : changer l'un ou l'autre change la portée, donc le store
 * ne rend plus rien pour la nouvelle (cf. `itemSelectionStore`). Sans cela une
 * sélection faite sur « apo » survivrait à la recherche suivante, ou resterait
 * active sur des lignes que l'onglet « Mails » ne montre plus — exactement ce que
 * la portée est censée empêcher.
 */
export const searchScope = (query: string, filter: string): string =>
  `search:${filter}:${query}`;

/**
 * Attributs posés sur CHAQUE ligne sélectionnable.
 *
 * POURQUOI LE DOM PORTE LES DONNÉES. Une plage MAJ a besoin de l'ordre VISUEL des
 * lignes et du contenu de celles qu'elle traverse. Dans l'arbre, aucun composant
 * ne connaît cet ordre : les dossiers se rendent récursivement et les mails de
 * chaque dossier viennent d'une requête distincte, montée à l'ouverture. Passer
 * l'ordre par un contexte obligerait à le recalculer à chaque dépliage, dans le
 * bon ordre, sans jamais se tromper — pour redire ce que le DOM sait déjà de
 * façon exacte. Le DOM EST l'ordre visuel ; on le lit plutôt que de le dupliquer.
 *
 * Ces attributs ne sont pas décoratifs : ils sont l'interface entre les lignes et
 * la sélection, et `refFromElement` en est la lecture inverse.
 */
export interface SelectableAttrs {
  'data-sel-scope': string;
  'data-sel-id': string;
  'data-sel-kind': SelectedKind;
  'data-sel-container': string;
  'data-sel-parent': string;
  'data-sel-label': string;
}

export function selectableAttrs(scope: string, ref: SelectedRef): SelectableAttrs {
  return {
    'data-sel-scope': scope,
    'data-sel-id': ref.id,
    'data-sel-kind': ref.kind,
    'data-sel-container': ref.containerId,
    // Chaîne vide et non « null » : un attribut ne porte que du texte, et
    // `'null'` serait un identifiant de dossier parfaitement plausible.
    'data-sel-parent': ref.parentId ?? '',
    'data-sel-label': ref.label,
  };
}

export function refFromElement(el: Element): SelectedRef | null {
  const id = el.getAttribute('data-sel-id');
  const kind = el.getAttribute('data-sel-kind');
  const containerId = el.getAttribute('data-sel-container');
  if (!id || !containerId || (kind !== 'mail' && kind !== 'folder')) return null;
  const parent = el.getAttribute('data-sel-parent');
  return {
    kind,
    id,
    containerId,
    parentId: parent ? parent : null,
    label: el.getAttribute('data-sel-label') ?? '',
  };
}

/**
 * Les lignes d'une portée, dans leur ordre d'affichage.
 *
 * `querySelectorAll` rend les nœuds dans l'ordre du document, qui est l'ordre
 * visuel dès lors qu'on ne réordonne rien en CSS — ce que l'add-in ne fait pas.
 * Les lignes repliées ne sont pas dans le DOM, donc une plage MAJ ne peut pas
 * emporter ce qui n'est pas visible : c'est exactement le comportement attendu.
 */
export function orderedRefs(scope: string, root: ParentNode = document): SelectedRef[] {
  const sel = `[data-sel-scope="${cssEscape(scope)}"][data-sel-id]`;
  return Array.from(root.querySelectorAll(sel))
    .map(refFromElement)
    .filter((r): r is SelectedRef => r !== null);
}

/**
 * Échappement pour un sélecteur d'attribut. Les portées contiennent des uuid et
 * un préfixe (« detail:… »), donc rien de dangereux aujourd'hui — mais un
 * sélecteur construit par concaténation sans échappement est le genre de dette
 * qui explose le jour où la valeur change de forme.
 */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

/**
 * Retire les éléments qu'un AUTRE élément sélectionné emporte déjà.
 *
 * LE CAS QUI L'IMPOSE : sélectionner un dossier et un mail qui s'y trouve, puis
 * déplacer. Sans élagage, le serveur reçoit deux ordres — « déplace le dossier »
 * et « déplace ce mail » — dont le second sort le mail du dossier qui vient de
 * partir avec lui. Le résultat dépend alors de l'ordre des appels, ce qui est la
 * définition d'un défaut intermittent. L'explorateur Windows fait le même
 * élagage, silencieusement.
 *
 * `parentOf` rend le parent d'un dossier, ou `null` s'il est à la racine ou
 * inconnu. Le parcours est borné par un ensemble de visités : une arborescence
 * incohérente (cycle) ne doit pas boucler ici — la garde existe aussi côté
 * serveur, mais un cycle en cache suffirait à figer l'interface.
 */
export function pruneRedundant(
  refs: readonly SelectedRef[],
  parentOf: (folderId: string) => string | null,
): SelectedRef[] {
  const selectedFolders = new Set(refs.filter((r) => r.kind === 'folder').map((r) => r.id));
  if (selectedFolders.size === 0) return [...refs];

  /** Un ancêtre (strict) de ce dossier est-il sélectionné ? */
  const underSelectedFolder = (startId: string | null): boolean => {
    const seen = new Set<string>();
    let cursor = startId;
    while (cursor && !seen.has(cursor)) {
      if (selectedFolders.has(cursor)) return true;
      seen.add(cursor);
      cursor = parentOf(cursor);
    }
    return false;
  };

  return refs.filter((r) => !underSelectedFolder(r.parentId));
}

/** Résumé d'une action de lot, tel qu'un toast doit le dire. */
export interface BatchOutcome {
  ref: SelectedRef;
  status: 'done' | 'skipped' | 'error';
  /** Motif, pour `skipped` comme pour `error`. Jamais un identifiant nu. */
  message?: string;
}

/**
 * Phrase de compte rendu d'un lot.
 *
 * DIT LES TROIS ISSUES SÉPARÉMENT. Un lot n'est presque jamais tout-ou-rien : un
 * mail peut être refusé pour doublon dans le dossier cible pendant que les quatre
 * autres passent. Un toast vert « déplacé » mentirait, un toast rouge « échec »
 * aussi — et l'utilisateur ne saurait pas lesquels reprendre. Les éléments en
 * échec sont donc NOMMÉS.
 */
export function summarizeBatch(
  outcomes: readonly BatchOutcome[],
  verb: string,
  carried = 0,
): string {
  const done = outcomes.filter((o) => o.status === 'done').length;
  const skipped = outcomes.filter((o) => o.status === 'skipped');
  const failed = outcomes.filter((o) => o.status === 'error');

  const parts: string[] = [`${done} ${verb}${done > 1 ? 's' : ''}`];
  // Les éléments emportés par un dossier sélectionné n'ont pas d'appel propre,
  // donc pas d'issue — mais les taire ferait lire « 1 déplacé » pour cinq lignes
  // cochées, ce qui se lit comme une perte.
  if (carried > 0) {
    parts.push(`${carried} ${carried > 1 ? 'ont suivi leur dossier' : 'a suivi son dossier'}`);
  }
  if (skipped.length > 0) {
    // Le motif du premier suffit : dans un lot, les « sans effet » ont
    // presque toujours la même cause, et l'énumérer noierait l'essentiel.
    const why = skipped[0]?.message;
    parts.push(`${skipped.length} sans effet${why ? ` (${why})` : ''}`);
  }
  if (failed.length > 0) {
    // Au plus deux noms : au-delà le toast devient un mur, et le détail se
    // retrouve dans la vue elle-même, où les éléments sont restés en place.
    const names = failed
      .slice(0, 2)
      .map((o) => `« ${o.ref.label} »${o.message ? ` (${o.message})` : ''}`)
      .join(', ');
    const rest = failed.length > 2 ? ` et ${failed.length - 2} autre(s)` : '';
    parts.push(`${failed.length} en échec : ${names}${rest}`);
  }
  return parts.join(', ');
}
